/**
 * NBDY hero — "Featured product" widget controls.
 *
 * - Prev/next cycle through product blocks.
 * - Cart link points at the active product's URL.
 * - Optional autoplay: a CSS-driven progress bar runs for the configured
 *   duration; each iteration end advances to the next product. Pausing is
 *   handled in CSS via `:hover` / `:focus-within`.
 */
class NbdyNowPlaying extends HTMLElement {
  connectedCallback() {
    this.tracks = Array.from(this.querySelectorAll('.nbdy-hero__np-track'));
    if (this.tracks.length === 0) return;

    this.cartButton = this.querySelector('[data-action="cart"]');
    this.progressFill = this.querySelector('.nbdy-hero__np-progress-fill');
    this.currentIndex = 0;

    // Autoplay only makes sense with 2+ products. Disable on the host element
    // so CSS hides the progress bar.
    if (this.tracks.length < 2) {
      this.removeAttribute('data-autoplay');
    }

    this.tracks[0].setAttribute('data-active', '');
    this.setAttribute('data-ready', '');
    this.#updateCartLink();

    this.addEventListener('click', this.#handleClick);
    this.progressFill?.addEventListener('animationiteration', this.#handleAutoAdvance);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.progressFill?.removeEventListener('animationiteration', this.#handleAutoAdvance);
  }

  #handleClick = (event) => {
    const button = event.target.closest('[data-action]');
    if (!button) return;

    switch (button.dataset.action) {
      case 'prev':
        this.#cycle(-1);
        this.#restartProgress();
        break;
      case 'next':
        this.#cycle(1);
        this.#restartProgress();
        break;
    }
  };

  #handleAutoAdvance = () => {
    this.#cycle(1);
  };

  #cycle(direction) {
    const next = (this.currentIndex + direction + this.tracks.length) % this.tracks.length;
    this.tracks[this.currentIndex].removeAttribute('data-active');
    this.tracks[next].setAttribute('data-active', '');
    this.currentIndex = next;
    this.#updateCartLink();
  }

  #updateCartLink() {
    if (!this.cartButton) return;
    const url = this.tracks[this.currentIndex].dataset.productUrl;
    if (url) this.cartButton.href = url;
  }

  #restartProgress() {
    if (!this.progressFill) return;
    // Toggle the animation off and on to reset the timeline.
    this.progressFill.style.animation = 'none';
    void this.progressFill.offsetWidth;
    this.progressFill.style.animation = '';
  }
}

if (!customElements.get('nbdy-now-playing')) {
  customElements.define('nbdy-now-playing', NbdyNowPlaying);
}
